/* CesarFilho 932048016 */
/* frontend/src/store/navStore.ts */

/**
 * Movatic WMS — Store da navegação lateral
 *
 * Controla:
 * - Grupos abertos/fechados do menu lateral
 * - Favoritos
 * - Sidebar compacta/expandida
 *
 * Observação:
 * - Usamos chaves v2 no localStorage para ignorar estados antigos que deixavam
 *   todos os grupos fechados.
 */

import { create } from 'zustand'
import { useAuthStore } from './authStore'

type State = {
    collapsedSections: Record<string, boolean>
    favorites: string[]
    sidebarCollapsed: boolean
    toggleSection: (id: string) => void
    openSection: (id: string) => void
    closeSection: (id: string) => void
    toggleFavorite: (id: string) => void
    setSidebarCollapsed: (value: boolean) => void
    resetNavigation: () => void
}

function load<T>(key: string, fallback: T): T {
    try {
        const value = localStorage.getItem(key)
        return value ? JSON.parse(value) as T : fallback
    } catch {
        return fallback
    }
}

function save(key: string, value: unknown) {
    try {
        localStorage.setItem(key, JSON.stringify(value))
    } catch {
        // Ignora erros de storage, por exemplo modo privado do navegador.
    }
}

function storageKeys() {
    const user = useAuthStore.getState().user
    const suffix = user?.username ? `:${user.username}` : ''

    return {
        collapsedKey: `movatic.nav.collapsed.v2${suffix}`,
        favoritesKey: `movatic.nav.favorites.v2${suffix}`,
        sidebarKey: `movatic.nav.sidebar.v2${suffix}`,
    }
}

export const useNavStore = create<State>((set, _get) => {
    const { collapsedKey, favoritesKey, sidebarKey } = storageKeys()

    return {
        /**
         * {} significa:
         * - nenhuma secção foi fechada manualmente
         * - o componente SidebarNav pode abrir tudo por padrão
         */
        collapsedSections: load<Record<string, boolean>>(collapsedKey, {}),

        favorites: load<string[]>(favoritesKey, []),

        sidebarCollapsed: load<boolean>(sidebarKey, false),

        toggleSection: (id: string) => {
            set((state) => {
                const currentValue = state.collapsedSections[id]

                /**
                 * Se ainda não existe estado salvo, o menu visualmente começa aberto.
                 * Portanto, o primeiro clique deve fechar.
                 */
                const nextValue = typeof currentValue === 'boolean'
                    ? !currentValue
                    : true

                const next = {
                    ...state.collapsedSections,
                    [id]: nextValue,
                }

                save(storageKeys().collapsedKey, next)
                return { collapsedSections: next }
            })
        },

        openSection: (id: string) => {
            set((state) => {
                const next = {
                    ...state.collapsedSections,
                    [id]: false,
                }

                save(storageKeys().collapsedKey, next)
                return { collapsedSections: next }
            })
        },

        closeSection: (id: string) => {
            set((state) => {
                const next = {
                    ...state.collapsedSections,
                    [id]: true,
                }

                save(storageKeys().collapsedKey, next)
                return { collapsedSections: next }
            })
        },

        toggleFavorite: (id: string) => {
            set((state) => {
                const exists = state.favorites.includes(id)

                const next = exists
                    ? state.favorites.filter((itemId) => itemId !== id)
                    : [...state.favorites, id]

                save(storageKeys().favoritesKey, next)
                return { favorites: next }
            })
        },

        setSidebarCollapsed: (value: boolean) => {
            save(storageKeys().sidebarKey, value)
            set({ sidebarCollapsed: value })
        },

        resetNavigation: () => {
            const keys = storageKeys()

            try {
                localStorage.removeItem(keys.collapsedKey)
                localStorage.removeItem(keys.favoritesKey)
                localStorage.removeItem(keys.sidebarKey)
            } catch {
                // Ignora erro de localStorage.
            }

            set({
                collapsedSections: {},
                favorites: [],
                sidebarCollapsed: false,
            })
        },
    }
})